import React, { useState, useEffect } from 'react';
import { captureError } from '@/utils/errorHandler';

export default function AdminCreateAppModal({ isOpen, onClose, onSuccess, authFetch, showToast }) {
  const [formData, setFormData] = useState({
    userEmail: '',
    appName: '',
    appDescription: '',
    appUrl: '',
    tags: '',
    amount: '',
    paymentMethod: 'upfront',
    monthlyMaintenanceFee: '0.00',
    appId: '', // Optional: UUID for importing existing apps
    makeMeOwner: false, // Optional: if true, admin becomes the app owner
  });
  const [submitting, setSubmitting] = useState(false);
  const [createdResult, setCreatedResult] = useState(null); // { app, user, checkoutUrl, emailSent }

  const resetForm = () => {
    setFormData({
      userEmail: '',
      appName: '',
      appDescription: '',
      appUrl: '',
      tags: '',
      amount: '',
      paymentMethod: 'upfront',
      monthlyMaintenanceFee: '0.00',
      appId: '',
      makeMeOwner: false,
    });
    setCreatedResult(null);
  };

  const handleCopyLink = () => {
    if (createdResult?.checkoutUrl) {
      navigator.clipboard.writeText(createdResult.checkoutUrl);
      showToast('Link copied to clipboard', 'success');
    }
  };

  const handleDone = () => {
    onSuccess && onSuccess(createdResult);
    onClose();
    resetForm();
  };

  // Reset when modal closes so we get a fresh form next time
  useEffect(() => {
    if (!isOpen) {
      setCreatedResult(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Show success state with payment link when app was created with checkout
  if (createdResult?.checkoutUrl) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 max-w-lg w-full">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">App Created!</h2>
          <p className="text-gray-600 mb-4">
            {createdResult.emailSent
              ? `Payment setup email sent to ${createdResult.user?.email}.`
              : 'Share the payment link below with your client.'}
          </p>
          <div className="flex gap-2 mb-6">
            <input
              type="text"
              readOnly
              value={createdResult.checkoutUrl}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono truncate"
            />
            <button
              type="button"
              onClick={handleCopyLink}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium whitespace-nowrap"
            >
              Copy Link
            </button>
            <a
              href={createdResult.checkoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium whitespace-nowrap"
            >
              Open Link
            </a>
          </div>
          <button
            type="button"
            onClick={handleDone}
            className="w-full px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If makeMeOwner is checked, userEmail is optional (will use admin email)
    // Otherwise, userEmail is required
    if (!formData.appName) {
      showToast('App name is required', 'error');
      return;
    }
    
    if (!formData.makeMeOwner && !formData.userEmail) {
      showToast('User email is required when "Make me the app owner" is not checked', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const tagsArray = formData.tags
        ? formData.tags.split(',').map(t => t.trim()).filter(t => t)
        : [];

      const response = await authFetch('/api/admin/create-app-for-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
          userEmail: formData.userEmail,
          appName: formData.appName,
          appDescription: formData.appDescription || null,
          appUrl: formData.appUrl || null,
          tags: tagsArray,
          amount: formData.amount ? parseFloat(formData.amount) : null,
          paymentMethod: formData.paymentMethod,
          monthlyMaintenanceFee: formData.monthlyMaintenanceFee ? parseFloat(formData.monthlyMaintenanceFee) : 0,
          appId: formData.appId?.trim() || null, // Optional UUID for importing existing apps
          makeMeOwner: formData.makeMeOwner, // Optional: make admin the app owner
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create app');
      }

      const data = await response.json();
      
      if (data.checkoutUrl) {
        setCreatedResult(data);
        showToast('✅ App created! Payment link ready.', 'success');
      } else {
        showToast('✅ App created successfully!', 'success');
        onSuccess && onSuccess(data);
        onClose();
        resetForm();
      }
    } catch (error) {
      captureError(error, {
        action: 'handleSubmit',
        component: 'AdminCreateAppModal',
        extra: { formData },
      });
      console.error('Error creating app:', error);
      showToast(error.message || 'Failed to create app', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Create App for User</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign to User (Email) {!formData.makeMeOwner && <span className="text-red-500">*</span>}
            </label>
            <input
              type="email"
              value={formData.userEmail}
              onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder={formData.makeMeOwner ? "Leave empty to use your email" : "user@example.com"}
              required={!formData.makeMeOwner}
              disabled={formData.makeMeOwner}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.makeMeOwner 
                ? "If you're the owner, this field is optional. Your email will be used for billing/assignment."
                : "The app will be assigned to this user. User account will be created automatically if it doesn't exist."}
            </p>
          </div>

          <div className="flex items-start gap-3 pt-1 pb-2">
            <input
              type="checkbox"
              id="makeMeOwner"
              checked={formData.makeMeOwner}
              onChange={(e) => setFormData({ ...formData, makeMeOwner: e.target.checked })}
              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <div className="flex-1">
              <label htmlFor="makeMeOwner" className="block text-sm font-medium text-gray-700 mb-1">
                Make me the app owner
              </label>
              <p className="text-xs text-gray-500">
                If checked, you (the admin) will be the owner of this app. The user email will still be used for billing/assignment purposes, but you will own the app.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.appName}
              onChange={(e) => setFormData({ ...formData, appName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="My Awesome App"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.appDescription}
              onChange={(e) => setFormData({ ...formData, appDescription: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="What does this app do?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">App URL</label>
            <input
              type="url"
              value={formData.appUrl}
              onChange={(e) => setFormData({ ...formData, appUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="https://app.example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="tag1, tag2, tag3 (comma-separated)"
            />
            <p className="mt-1 text-xs text-gray-500">For admin organization/search</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App ID (UUID) <span className="text-gray-400 font-normal">(Optional - for importing existing apps)</span>
            </label>
            <input
              type="text"
              value={formData.appId}
              onChange={(e) => setFormData({ ...formData, appId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
              placeholder="00000000-0000-0000-0000-000000000000"
            />
            <p className="mt-1 text-xs text-gray-500">
              If you have an existing app with a UUID you want to preserve, enter it here. Leave empty to generate a new UUID.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Payment (Optional)</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="upfront">Upfront Payment</option>
                </select>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              If amount is provided, a checkout session will be created for the user
            </p>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Monthly Maintenance Fee</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Fee ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.monthlyMaintenanceFee}
                onChange={(e) => setFormData({ ...formData, monthlyMaintenanceFee: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="0.00"
              />
              <p className="mt-1 text-xs text-gray-500">
                Set to 0.00 to disable monthly maintenance billing for this app
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50 font-medium"
            >
              {submitting ? 'Creating...' : 'Create App'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

